import { useEffect, useId, useRef, useState } from 'react'
import { Check, Loader2, Pencil, X } from 'lucide-react'
import { Button, Input } from '@atrvd/saas-ui'
import { formatCurrency } from '@dcp/shared/utils/formatters'
import { useUpdateBankBalanceManual } from '@/hooks/useBanking'

/**
 * Money-shaped input regex: optional leading `-`, at least one integer
 * digit, optionally followed by `.` plus 1–2 fractional digits. Matches
 * the precision the `bank_balance DECIMAL(15,2)` column actually accepts
 * and rejects values like `12345.678` that previously slipped past
 * `Number.isFinite` (HTML `step="0.01"` was a UI hint only, never a
 * runtime guard).
 */
const MONEY_INPUT_RE = /^-?\d+(\.\d{1,2})?$/

interface EditableBalanceCellProps {
  /** UUID of the bank account this cell mutates */
  bankAccountId: string
  /** Current balance displayed when not editing */
  value: number
  /** When true, the row is API-backed and must stay read-only */
  isApiAccount: boolean
}

/**
 * Bank Balance cell with inline edit for manual (non-API) accounts.
 *
 * Read-only path: formats `value` with parenthesised negatives in red — the
 * convention shared with `formatCategoryAmount` for spending-style numbers.
 *
 * Edit path (manual accounts only): pencil affordance flips the cell into a
 * numeric input with Save/Cancel controls. Save calls
 * {@link useUpdateBankBalanceManual} and exits on success. Empty input
 * surfaces an inline "Required" message and blocks the mutation (AC#6).
 *
 * The mutation invalidates the balance-import-status query, so the new
 * `lastUpdateSource` ("Manual") and `lastUpdateTimestamp` come back from
 * the server on the refetch — we deliberately don't optimistically patch
 * those fields here.
 */
export function EditableBalanceCell({ bankAccountId, value, isApiAccount }: EditableBalanceCellProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  // Stable id pair so the input can declare `aria-describedby={errorId}`
  // when an inline error is shown — screen readers then announce the
  // error message alongside the field instead of leaving it as orphaned
  // text. useId guarantees uniqueness across multiple cells on the page.
  const errorId = useId()

  const updateMutation = useUpdateBankBalanceManual()

  // Auto-focus the input when entering edit mode so the user can type
  // immediately — matches the standard inline-edit affordance and keeps the
  // keyboard flow (Tab from neighbouring cells → Enter to save).
  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus()
      inputRef.current?.select()
    }
  }, [isEditing])

  // Render path 1: API account — read-only.
  if (isApiAccount) {
    return <span className={cellClass(value)}>{formatBalance(value)}</span>
  }

  function startEditing() {
    setDraft(String(value))
    setError(null)
    setIsEditing(true)
  }

  function cancelEditing() {
    setDraft('')
    setError(null)
    setIsEditing(false)
  }

  async function commitEditing() {
    // Re-entrancy guard: the input's onKeyDown handler reacts to Enter
    // synchronously, so a double-press (or holding Enter) could fire two
    // commitEditing calls before the first finishes — both would race on
    // the same (bank_account_id, today) upsert. The Save Button is
    // already `disabled={isPending}` which blocks mouse clicks, but the
    // keyboard path bypasses Button's disabled gating. Short-circuit
    // here so the keyboard and pointer paths agree.
    if (updateMutation.isPending) return

    // AC#6 — empty value must not save; surface an inline message instead.
    const trimmed = draft.trim()
    if (trimmed === '') {
      setError('Required')
      return
    }
    // Numeric coercion first so the failure message tracks the reason.
    // Non-numeric input ("abc") fails Number.isFinite and surfaces the
    // generic "valid number" hint, while values that parse to a finite
    // number (e.g. "12.345") fall through to the precision regex below
    // and get the precision-specific message. Reversing this order would
    // show "up to 2 decimal places" for "abc", which is misleading.
    const parsed = Number(trimmed)
    if (!Number.isFinite(parsed)) {
      setError('Enter a valid number')
      return
    }

    // Shape check — enforces the same 2-decimal precision the
    // `bank_balance DECIMAL(15,2)` column will accept. The HTML input no
    // longer carries `step="0.01"` (we moved to type=text for money UX),
    // so this regex is the canonical place to reject `12345.678`.
    if (!MONEY_INPUT_RE.test(trimmed)) {
      setError('Enter an amount with up to 2 decimal places')
      return
    }

    // No-op short-circuit: opening edit mode and pressing Save without
    // changing the value would still trigger an upsert + cache invalidation
    // + table refetch, briefly flickering the row. When the parsed value
    // matches what's already displayed, treat the action as a cancel.
    if (parsed === value) {
      cancelEditing()
      return
    }

    try {
      await updateMutation.mutateAsync({ bankAccountId, balance: parsed })
      setIsEditing(false)
      setDraft('')
      setError(null)
    } catch (e) {
      // Mutation failure: keep the user in edit mode so they can retry without
      // losing what they typed; the server-side message is the most actionable.
      setError(e instanceof Error ? e.message : 'Failed to save')
    }
  }

  // Render path 2: editing — input + save/cancel.
  if (isEditing) {
    return (
      // relative container so the absolutely-positioned error message sits
      // directly below the input row without pushing the table row taller.
      <div className="relative inline-flex flex-col">
        <div className="flex items-center gap-1">
          <Input
            ref={inputRef}
            // Money fields are better off as text + inputMode="decimal":
            // - type="number" spinners can change the value via mouse
            //   wheel on focus, which is a silent data-corruption risk
            //   for a banking flow.
            // - Cross-browser behaviour for partial input ('-' before a
            //   digit, lone '.' etc.) is inconsistent under type=number.
            // - inputMode="decimal" still pops the numeric keypad on
            //   mobile — the actual UX we wanted from type=number.
            // Precision is enforced by MONEY_INPUT_RE in commitEditing,
            // not by the browser.
            type="text"
            inputMode="decimal"
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value)
              if (error) setError(null)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                void commitEditing()
              } else if (e.key === 'Escape') {
                e.preventDefault()
                cancelEditing()
              }
            }}
            disabled={updateMutation.isPending}
            aria-label="New bank balance"
            aria-invalid={error !== null}
            // aria-busy lets assistive tech announce "busy" while the
            // upsert is in flight (input is also visually disabled).
            aria-busy={updateMutation.isPending}
            // Bind the error message to the input so screen readers
            // narrate it on focus. The describedby target only exists
            // while `error` is set — undefined here keeps the attribute
            // off the DOM in the happy path so it doesn't dangle.
            aria-describedby={error ? errorId : undefined}
            className="h-8 w-32"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => void commitEditing()}
            disabled={updateMutation.isPending}
            aria-label="Save balance"
            className="h-8 w-8"
          >
            {/* While the upsert is in flight, swap the checkmark for a
                spinning loader so the user has a visible signal that the
                click registered. aria-busy on the input already announces
                this to screen readers; the icon swap is for sighted users. */}
            {updateMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Check className="h-4 w-4" />
            )}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={cancelEditing}
            disabled={updateMutation.isPending}
            aria-label="Cancel balance edit"
            className="h-8 w-8"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        {error && (
          // Absolutely positioned so the message floats below the input
          // without growing the table row height and shifting sibling cells.
          // role="alert" + aria-live="assertive" so screen readers announce
          // the message as soon as it appears (WCAG 2.1 SC 4.1.3, Status
          // Messages). The id matches aria-describedby on the input above.
          <span
            id={errorId}
            role="alert"
            aria-live="assertive"
            className="absolute left-0 top-full mt-0.5 text-xs text-red-600 whitespace-nowrap"
          >
            {error}
          </span>
        )}
      </div>
    )
  }

  // Render path 3: manual account, not editing — value + pencil always visible.
  // DCP-363: hover-only affordance was not discoverable; pencil is rendered
  // permanently so keyboard users can tab to it and discover the edit action.
  return (
    <div className="flex items-center gap-1">
      <span className={cellClass(value)}>{formatBalance(value)}</span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={startEditing}
        aria-label="Edit bank balance"
        className="h-6 w-6 text-muted-foreground"
      >
        <Pencil className="h-3.5 w-3.5" />
      </Button>
    </div>
  )
}

/** Currency text helper — accounting-style parentheses for negatives. */
function formatBalance(value: number): string {
  return formatCurrency(value, { negativeStyle: 'parentheses' })
}

/** Red text for negative values; default colour otherwise. */
function cellClass(value: number): string {
  return value < 0 ? 'font-medium text-red-600' : 'font-medium'
}
